<script setup lang="ts">
import type { WorkflowEdge, WorkflowNode } from '@/api/modules/workflow'
import { FullScreen, ZoomIn, ZoomOut } from '@element-plus/icons-vue'
import LogicFlow from '@logicflow/core'
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'
import NodePropertyPanel from './NodePropertyPanel.vue'
import '@logicflow/core/dist/index.css'

// ==================== Props & Emits ====================
interface Props {
  modelValue?: {
    nodes: WorkflowNode[]
    edges: WorkflowEdge[]
  }
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({ nodes: [], edges: [] }),
})

const emit = defineEmits<{
  'update:modelValue': [value: { nodes: WorkflowNode[], edges: WorkflowEdge[] }]
  'save': [value: { nodes: WorkflowNode[], edges: WorkflowEdge[] }]
}>()

// ==================== 状态定义 ====================
const containerRef = ref<HTMLElement>()
const lf = shallowRef<LogicFlow>()
const selectedNode = ref<any>(null)
const canUndo = ref(false)
const canRedo = ref(false)

// 节点类型配置
const nodeTypes = [
  { type: 'start', label: '开始', icon: 'VideoPlay' },
  { type: 'approval', label: '审批', icon: 'User' },
  { type: 'condition', label: '条件', icon: 'Operation' },
  { type: 'cc', label: '抄送', icon: 'Message' },
  { type: 'end', label: '结束', icon: 'CircleClose' },
]

// ==================== 方法定义 ====================

// 初始化 LogicFlow
function initLogicFlow() {
  if (!containerRef.value) {
    return
  }

  lf.value = new LogicFlow({
    container: containerRef.value,
    grid: true,
    background: {
      color: '#f7f9ff',
    },
    keyboard: {
      enabled: true,
    },
  })

  // 注册自定义节点（后续实现）
  // registerCustomNodes(lf.value)

  // 监听节点选中事件
  lf.value.on('node:click', ({ data }) => {
    selectedNode.value = data
  })

  // 监听画布点击事件
  lf.value.on('blank:click', () => {
    selectedNode.value = null
  })

  // 加载初始数据
  if (props.modelValue.nodes.length > 0) {
    lf.value.render(props.modelValue as any)
  }
}

// 节点拖拽开始
function handleNodeDragStart(event: DragEvent, node: any) {
  event.dataTransfer!.setData('nodeType', node.type)
}

// 放大
function handleZoomIn() {
  lf.value?.zoom(true)
}

// 缩小
function handleZoomOut() {
  lf.value?.zoom(false)
}

// 适应画布
function handleFitView() {
  lf.value?.fitView()
}

// 撤销
function handleUndo() {
  lf.value?.undo()
}

// 重做
function handleRedo() {
  lf.value?.redo()
}

// 保存
function handleSave() {
  const data = lf.value?.getGraphData()
  if (data) {
    emit('save', data as any)
  }
}

// 更新节点属性
function handleNodeUpdate(properties: any) {
  if (selectedNode.value) {
    lf.value?.setProperties(selectedNode.value.id, properties)
  }
}

// ==================== 生命周期 ====================
onMounted(() => {
  initLogicFlow()
})

onBeforeUnmount(() => {
  lf.value?.destroy()
})
</script>

<template>
  <div class="workflow-designer">
    <!-- 工具栏 -->
    <div class="designer-toolbar">
      <div class="toolbar-left">
        <el-button-group>
          <el-button :icon="ZoomIn" @click="handleZoomIn">
            放大
          </el-button>
          <el-button :icon="ZoomOut" @click="handleZoomOut">
            缩小
          </el-button>
          <el-button :icon="FullScreen" @click="handleFitView">
            适应画布
          </el-button>
        </el-button-group>
      </div>
      <div class="toolbar-right">
        <el-button :disabled="!canUndo" @click="handleUndo">
          撤销
        </el-button>
        <el-button :disabled="!canRedo" @click="handleRedo">
          重做
        </el-button>
        <el-button type="primary" @click="handleSave">
          保存
        </el-button>
      </div>
    </div>

    <!-- 主体区域 -->
    <div class="designer-main">
      <!-- 左侧节点面板 -->
      <div class="node-panel">
        <div class="panel-title">
          节点类型
        </div>
        <div class="node-list">
          <div
            v-for="node in nodeTypes"
            :key="node.type"
            class="node-item"
            draggable="true"
            @dragstart="handleNodeDragStart($event, node)"
          >
            <el-icon :size="24">
              <component :is="node.icon" />
            </el-icon>
            <span>{{ node.label }}</span>
          </div>
        </div>
      </div>

      <!-- 中间画布区域 -->
      <div class="canvas-container">
        <div ref="containerRef" class="lf-container" />
      </div>

      <!-- 右侧属性面板 -->
      <div class="property-panel">
        <div class="panel-title">
          节点属性
        </div>
        <div v-if="selectedNode" class="property-content">
          <NodePropertyPanel
            :node="selectedNode"
            @update="handleNodeUpdate"
          />
        </div>
        <div v-else class="empty-tip">
          <el-empty description="请选择节点" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.workflow-designer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;

  .designer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
  }

  .designer-main {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .node-panel {
    width: 200px;
    overflow-y: auto;
    background: #fafafa;
    border-right: 1px solid #e4e7ed;

    .panel-title {
      padding: 12px 16px;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #e4e7ed;
    }

    .node-list {
      padding: 12px;
    }

    .node-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 16px;
      margin-bottom: 12px;
      cursor: move;
      background: #fff;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 8px rgb(64 158 255 / 20%);
      }

      span {
        margin-top: 8px;
        font-size: 12px;
        color: #606266;
      }
    }
  }

  .canvas-container {
    position: relative;
    flex: 1;
    overflow: hidden;

    .lf-container {
      width: 100%;
      height: 100%;
    }
  }

  .property-panel {
    width: 320px;
    overflow-y: auto;
    background: #fafafa;
    border-left: 1px solid #e4e7ed;

    .panel-title {
      padding: 12px 16px;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      border-bottom: 1px solid #e4e7ed;
    }

    .property-content {
      padding: 16px;
    }

    .empty-tip {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 300px;
    }
  }
}
</style>
